import { useEffect } from "react";
import { Marker, Popup, Polyline, CircleMarker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import { STATUS, courierIcon, timeAgo, fmtTime } from "./courierMapUtils";

export const FitTo = ({ points, trigger }) => {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const b = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    if (b.isValid()) map.fitBounds(b, { padding: [50, 50], maxZoom: 15 });
  }, [trigger]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

export const CourierMarkers = ({ couriers, selectedId, onSelect }) =>
  couriers.filter((c) => c.lat != null).map((c) => {
    const s = STATUS[c.status] || STATUS.offline;
    return (
      <Marker key={c.user_id} position={[c.lat, c.lng]} icon={courierIcon(c, c.user_id === selectedId)}
        zIndexOffset={c.user_id === selectedId ? 1000 : 0} eventHandlers={{ click: () => onSelect(c.user_id) }}>
        <Popup>
          <div className="space-y-1 text-sm min-w-[180px]" data-testid={`courier-popup-${c.user_id}`}>
            <div className="font-black uppercase tracking-tight">{c.name}</div>
            <div className="text-xs font-bold" style={{ color: s.color }}>{s.label} · {timeAgo(c.updated_at)}</div>
            {c.idle_alert && <div className="text-xs font-bold text-red-600">Postój {c.idle_min} min w trakcie trasy</div>}
            {c.progress?.total ? (
              <div className="text-xs pt-1 border-t border-zinc-200">
                Trasa: <b>{c.progress.done}/{c.progress.total}</b> dostarczone, {c.progress.pending} pozostało
                {c.progress.next_address && <div className="text-zinc-500 mt-0.5">Następna: {c.progress.next_address}</div>}
                {c.progress.eta && <div className="mt-0.5">Koniec ok. <b>{fmtTime(c.progress.eta)}</b> <span className="text-zinc-400">(~{c.progress.pace_min} min/paczka)</span></div>}
              </div>
            ) : <div className="text-xs text-zinc-500 pt-1 border-t border-zinc-200">Brak trasy na dziś</div>}
            <div className="text-xs">Prędkość: <b>{Math.round(c.speed_kmh || 0)} km/h</b>{c.accuracy != null && <span className="text-zinc-400"> · ±{Math.round(c.accuracy)} m</span>}</div>
          </div>
        </Popup>
      </Marker>
    );
  });

const stopIcon = (s) => {
  const bg = s.status === "done" ? "#059669" : s.status === "skipped" ? "#E52C34" : "#ffffff";
  const fg = s.status === "pending" ? "#18181b" : "#ffffff";
  const border = s.status === "pending" ? "#18181b" : "#ffffff";
  return L.divIcon({
    html: `<div style="width:22px;height:22px;border-radius:9999px;background:${bg};color:${fg};border:2px solid ${border};display:flex;align-items:center;justify-content:center;font:800 10px/1 'Space Grotesk',system-ui,sans-serif;box-shadow:0 2px 6px rgba(0,0,0,.3)">${s.final_order ?? "•"}</div>`,
    className: "courier-marker", iconSize: [22, 22], iconAnchor: [11, 11],
  });
};

export const StopsLayer = ({ stops }) =>
  stops.filter((s) => s.lat != null).map((s, i) => (
    <Marker key={`${s.final_order}-${i}`} position={[s.lat, s.lng]} icon={stopIcon(s)} zIndexOffset={-100}>
      <Tooltip direction="top">
        <b>{s.status === "done" ? "Dostarczono" : s.status === "skipped" ? "Awizo" : "Do dostarczenia"}</b>
        {s.status_at ? ` ${fmtTime(s.status_at)}` : ""}<br />{s.final_order != null && <span className="font-mono">#{s.final_order} </span>}{s.address}
      </Tooltip>
    </Marker>
  ));

export const PlaybackMarker = ({ point, name }) => {
  if (!point) return null;
  const icon = L.divIcon({
    html: `<div style="width:30px;height:30px;border-radius:9999px;background:#18181b;border:3px solid #fff;box-shadow:0 0 0 4px rgba(229,44,52,.35),0 4px 12px rgba(0,0,0,.4)"></div>`,
    className: "courier-marker", iconSize: [30, 30], iconAnchor: [15, 15],
  });
  return (
    <Marker position={[point.lat, point.lng]} icon={icon} zIndexOffset={2000}>
      <Tooltip direction="top" permanent><b>{name}</b> · {fmtTime(point.ts)}</Tooltip>
    </Marker>
  );
};

export const TrailLayer = ({ trail, showStops = true, playIdx = null }) => {
  if (!trail) return null;
  const pts = trail.points.map((p) => [p.lat, p.lng]);
  const splitAt = playIdx != null ? Math.min(playIdx + 1, pts.length) : pts.length;
  const donePts = pts.slice(0, splitAt);
  const restPts = playIdx != null ? pts.slice(Math.max(splitAt - 1, 0)) : [];
  return (
    <>
      {donePts.length > 1 && <Polyline positions={donePts} pathOptions={{ color: "#18181b", weight: 6, opacity: 0.25 }} />}
      {donePts.length > 1 && <Polyline positions={donePts} pathOptions={{ color: "#E52C34", weight: 3, opacity: 0.9 }} />}
      {restPts.length > 1 && <Polyline positions={restPts} pathOptions={{ color: "#71717A", weight: 3, opacity: 0.5, dashArray: "6 8" }} />}
      {pts.length > 0 && (
        <CircleMarker center={pts[0]} radius={6} pathOptions={{ color: "#fff", weight: 2, fillColor: "#18181b", fillOpacity: 1 }}>
          <Tooltip direction="top">Start {fmtTime(trail.first_at)}</Tooltip>
        </CircleMarker>
      )}
      {showStops && <StopsLayer stops={trail.stops} />}
    </>
  );
};
